import { CustomNavHelper } from '@unifyapps/carbon/helpers/CustomNavHelper';
import { InterfaceNavigationHelper } from '@unifyapps/carbon/helpers/InterfaceNavigationHelper';
import {
  ApplicationType,
  type InterfaceEntity,
  NavigationResolution,
} from '@unifyapps/defs/types/interface';
import type { useLocation } from 'react-use';
import type { UserPermissionsMap } from '../../../access-control/types';
import type { InterfaceModes } from '../../../stores/InterfaceStore/types';
import { computeActiveNavItems } from './computeActiveNavItems';
import { createNavItemTransformer } from './navItemTransformer';
import type { NavigationForContext } from './navigationForContextTypes';

export type { ActiveNavItems, NavigationForContext } from './navigationForContextTypes';

type LocationInStoreForNavigation = Pick<
  ReturnType<typeof useLocation>,
  'href' | 'hostname' | 'pathname' | 'search' | 'hash'
> & {
  pathParams?: Record<string, string | string[] | undefined>;
};

type Props = {
  interfaceRecord: InterfaceEntity;
  resolution: NavigationResolution;
  applicationType?: ApplicationType;
  interfaceMode: InterfaceModes;
  permissions?: UserPermissionsMap;
  isNotificationPanelOpen?: boolean;
  unopenedNotificationsCount?: number;
  location?: LocationInStoreForNavigation;
  basePath?: string;
};

/**
 * Computes the full navigation object for context. All nav computation lives here;
 * context builder only receives and uses the result.
 * When pathname and projectSlug are provided (web), active item ids are computed and
 * included so they appear on interface record state (navigation.activeItem).
 */
export function getNavigationForContext({
  interfaceRecord,
  resolution,
  applicationType,
  interfaceMode,
  permissions,
  isNotificationPanelOpen = false,
  unopenedNotificationsCount = 0,
  location,
  basePath,
}: Props): NavigationForContext {
  const pathname = location?.pathname ? location.pathname.replace(basePath ?? '', '') : undefined;
  const rawProjectSlug = location?.pathParams?.projectSlug;

  const projectSlug = [rawProjectSlug].flat()[0];

  const effectiveResolution =
    applicationType === ApplicationType.MOBILE ? NavigationResolution.SM : resolution;

  const rawPrimary = CustomNavHelper.getPrimaryNavigationByResolution(
    interfaceRecord,
    effectiveResolution,
  );
  const rawSecondary =
    CustomNavHelper.getSecondaryNavigationByResolution(interfaceRecord, effectiveResolution) ?? [];

  const transformer = createNavItemTransformer({
    mode: interfaceMode,
    interfaceRecord,
    permissions,
    pagePermissions: interfaceRecord.properties.pagePermissions,
  });

  const primaryNavigation = transformer(rawPrimary);
  const secondaryNavigation = transformer(rawSecondary ?? []);

  const isCustomNavEnabled = CustomNavHelper.isEnabled(
    interfaceRecord,
    effectiveResolution,
    applicationType,
    true,
  );

  const navHeight = isCustomNavEnabled
    ? InterfaceNavigationHelper.getHeightOrWidthByResolution(
        interfaceRecord,
        effectiveResolution,
        undefined,
        applicationType,
        'height',
      )
    : undefined;
  const navWidth = isCustomNavEnabled
    ? InterfaceNavigationHelper.getHeightOrWidthByResolution(
        interfaceRecord,
        effectiveResolution,
        undefined,
        applicationType,
        'width',
      )
    : undefined;

  const activeItem =
    applicationType !== ApplicationType.MOBILE && pathname !== undefined && pathname !== ''
      ? computeActiveNavItems({
          primaryNavigation,
          secondaryNavigation: secondaryNavigation ?? [],
          pathname,
          projectSlug,
          isNotificationPanelOpen,
        })
      : undefined;

  const itemsBase = {
    primary: primaryNavigation,
    secondary: secondaryNavigation ?? [],
  };

  const navigation: NavigationForContext = {
    items:
      applicationType !== ApplicationType.MOBILE
        ? {
            ...itemsBase,
            brand: InterfaceNavigationHelper.getBrandNavigationItem(
              interfaceRecord,
              effectiveResolution,
            ),
            user: InterfaceNavigationHelper.getUserMenuItem(interfaceRecord, effectiveResolution),
          }
        : itemsBase,
    ...(activeItem !== undefined && { activeItem }),
    ...(isCustomNavEnabled &&
      navHeight !== undefined &&
      navWidth !== undefined && {
        dimensions: { height: navHeight, width: navWidth },
      }),
    ...(applicationType !== ApplicationType.MOBILE && {
      notifications: {
        isPanelOpen: isNotificationPanelOpen,
        unreadCount: unopenedNotificationsCount,
      },
    }),
  };

  return navigation;
}
